// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Paleta do mapa
// ============================================================================
//
// AS CORES DE CLIENTE SÃO GERADAS, e vêm de `paleta-clientes.ts`. A origem é
// `tools/validador-paleta/validar.py`, que as constrói em OKLCH e as valida antes
// de as escrever. Não se escolhem aqui e não se editam à mão.
//
// O SISTEMA, EM UMA LINHA
//   quente = problema · verde = conforme · frio e púrpura = cliente
//
//   Não é decoração: é o que permite ao alarme saltar à vista. A escala de estado
//   é quente, e por isso nenhuma cor de cliente pode ser quente — se fosse,
//   competiria com ela e o olho seria puxado para onde não há problema nenhum.
//   As matizes quentes e o verde estão **excluídas por medição** da paleta de
//   cliente: a tampa verde ficava a ΔE 0,009 de `estado:bom` sob protanopia.
//
// O QUE A COR DE CLIENTE FAZ
//   Deixa varrer o mapa e ver onde cada bloco começa e acaba. **Não identifica o
//   cliente** — são 26 tons para ~40 clientes, e o validador confirma que nem 2
//   tampas seriam distinguíveis entre si sob visão reduzida. A identidade é a
//   **sigla**, sempre escrita na barra.
// ============================================================================

import { CLIENTES_CLARO, CLIENTES_ESCURO, N_CORES_CLIENTE, type CorCliente } from './paleta-clientes.js';

export type Tema = 'claro' | 'escuro';

/** Escala fixa de estado. Sempre com etiqueta ou ícone, nunca cor sozinha. */
export const ESTADO = {
  bom: '#0ca30c',
  aviso: '#fab219',
  grave: '#ec835a',
  critico: '#d03b3b',
} as const;

/**
 * Dia de rotação.
 *
 * Na folha actual é `#000000`. Aqui é um quase-preto: o preto puro sobre
 * superfície clara dá 21:1, acima do que qualquer norma pede e desconfortável em
 * blocos. O que importa preservar é «é a célula escura», e isso mantém-se.
 */
export const ROTACAO = { claro: '#1b1b1a', escuro: '#0c0c0b' } as const;

/** Barra de um cliente desligado no filtro. Continua a dizer «há serviço aqui». */
export const ESBATIDO = { claro: '#e6e5df', escuro: '#2a2a27' } as const;
export const ESBATIDO_CAP = { claro: '#c3c2b7', escuro: '#4a4944' } as const;

export const TINTA_ESCURA = '#0b0b0b';
export const TINTA_CLARA = '#ffffff';

/**
 * A cor de um cliente.
 *
 * Clientes acima de `N_CORES_CLIENTE` reutilizam cores, e isso é legítimo: a cor
 * não é a identidade. Com `-1` (sem cliente) devolve o tom esbatido.
 */
export function corCliente(cliente: number, tema: Tema): CorCliente {
  if (cliente < 0) return { fundo: ESBATIDO[tema], cap: ESBATIDO_CAP[tema] };
  const tabela = tema === 'claro' ? CLIENTES_CLARO : CLIENTES_ESCURO;
  return (
    tabela[cliente % N_CORES_CLIENTE] ?? { fundo: ESBATIDO[tema], cap: ESBATIDO_CAP[tema] }
  );
}

/** Luminância relativa segundo a WCAG 2. */
export function luminancia(hex: string): number {
  const n = hex.replace('#', '');
  const componente = (i: number): number => {
    const v = parseInt(n.slice(i, i + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * componente(0) + 0.7152 * componente(2) + 0.0722 * componente(4);
}

export function contraste(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * Tinta legível sobre um fundo. Requisito V7.
 *
 * Escolhe-se **por medição**, comparando o contraste real das duas tintas. Não há
 * limiar de luminância fixo, de propósito: o valor 0,179 que se vê por aí é o
 * cruzamento contra **preto puro**, e a nossa tinta escura é `#0b0b0b`, o que
 * desloca o cruzamento para ≈0,187. A diferença é estreita mas real, e apanha
 * fundos como `#767676`, onde 0,179 escolheria escuro quando o branco contrasta
 * melhor (4,54 : 4,34).
 */
export function tintaSobre(fundo: string): string {
  const l = luminancia(fundo);
  return contraste(l, luminancia(TINTA_ESCURA)) >= contraste(l, luminancia(TINTA_CLARA))
    ? TINTA_ESCURA
    : TINTA_CLARA;
}
